/**
 * Custom stamps (toiyp Plus): which design goes on the letter being written.
 *
 * The choice is sticky — the member's last design stays on their next letters
 * until they pick another or go back to the plain class stamp — but only while
 * they can still use it: a design from a collection they no longer have (Plus
 * lapsed, a set retired) quietly falls back to the plain stamp. The server
 * checks it again at seal either way. A design never changes the class.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMyStampsQuery, type MyStamps, type StampCollection, type StampDesign, type Stationery } from '../letters/api';

const KEY = 'toiyp:stamp-design';
/** Last paper used, per correspondent: { [relationshipId]: stationeryId }. */
const PAPER_KEY = 'toiyp:paper-by-pair';
/**
 * Envelope chosen per correspondent: an id, or `plain` when they chose the
 * plain envelope on purpose. Absent means "match the paper's book".
 */
const ENVELOPE_KEY = 'toiyp:envelope-by-pair';

function readPair(key: string, relationshipId: string | undefined): string | null {
  if (!relationshipId) return null;
  try {
    return (JSON.parse(localStorage.getItem(key) ?? '{}') as Record<string, string>)[relationshipId] ?? null;
  } catch {
    return null;
  }
}

function writePair(key: string, relationshipId: string | undefined, id: string | null): void {
  if (!relationshipId) return;
  try {
    const map = JSON.parse(localStorage.getItem(key) ?? '{}') as Record<string, string>;
    if (id) map[relationshipId] = id;
    else delete map[relationshipId];
    localStorage.setItem(key, JSON.stringify(map));
  } catch {
    /* this visit only */
  }
}

const readPaper = (r: string | undefined) => readPair(PAPER_KEY, r);
const writePaper = (r: string | undefined, id: string | null) => writePair(PAPER_KEY, r, id);

/** The design this member last put on a letter (sticky across letters), or null. */
export function readChosen(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

function writeChosen(id: string | null): void {
  try {
    if (id) localStorage.setItem(KEY, id);
    else localStorage.removeItem(KEY);
  } catch {
    /* a private window: the choice lasts this visit only */
  }
}

/** Collections a member can stick on a letter (everything but locked previews). */
export function usableCollections(stamps: MyStamps | undefined): StampCollection[] {
  return (stamps?.collections ?? []).filter((c) => c.access !== 'locked');
}

/** The chosen design if it is still usable, else null (the plain class stamp). */
export function resolveDesign(stamps: MyStamps | undefined, chosenId: string | null): StampDesign | null {
  if (!chosenId) return null;
  for (const c of usableCollections(stamps)) {
    const d = c.designs.find((x) => x.id === chosenId);
    if (d) return d;
  }
  return null;
}

/** What a book holds, in a few words: "9 stamps · 1 letter paper". */
export function bookContents(c: Pick<StampCollection, 'designs' | 'papers'> & { envelopes?: unknown[] }): string {
  const parts: string[] = [];
  const envelopes = c.envelopes?.length ?? 0;
  if (c.designs.length) parts.push(`${c.designs.length} stamp${c.designs.length === 1 ? '' : 's'}`);
  if (c.papers.length) parts.push(`${c.papers.length} letter paper${c.papers.length === 1 ? '' : 's'}`);
  if (envelopes) parts.push(`${envelopes} envelope${envelopes === 1 ? '' : 's'}`);
  return parts.join(' · ');
}

/**
 * The envelope a letter goes in. A choice made for this correspondent wins
 * (`plain` included) while it is still usable; with no choice, the envelope
 * matches the paper — the first envelope of the paper's own book — so a set
 * travels together. Plain otherwise.
 */
export function resolveEnvelope(
  stamps: MyStamps | undefined,
  chosen: string | null,
  paper: Stationery | null,
): Stationery | null {
  if (chosen === 'plain') return null;
  const usable = usableCollections(stamps);
  if (chosen) {
    for (const c of usable) {
      const e = c.envelopes.find((x) => x.id === chosen);
      if (e) return e;
    }
  }
  if (!paper) return null;
  const book = usable.find((c) => c.papers.some((p) => p.id === paper.id));
  return book?.envelopes[0] ?? null;
}

/** The chosen paper if it is still usable, else null (plain paper). */
export function resolvePaper(stamps: MyStamps | undefined, chosenId: string | null): Stationery | null {
  if (!chosenId) return null;
  for (const c of usableCollections(stamps)) {
    const p = c.papers.find((x) => x.id === chosenId);
    if (p) return p;
  }
  return null;
}

export interface DesignChoice {
  stamps: MyStamps | undefined;
  /** The design going on this letter, or null for the plain class stamp. */
  design: StampDesign | null;
  chooseDesign: (id: string | null) => void;
  /** The member has any design they could use — the composer shows its stamp for them. */
  hasDesigns: boolean;
  /** The paper this letter is written on, or null for plain paper. Remembered per correspondent. */
  paper: Stationery | null;
  choosePaper: (id: string | null) => void;
  /** Any paper is on offer (usable or a locked preview) — the picker shows its Paper shelf. */
  hasPapers: boolean;
  /** The envelope this letter goes in, or null for a plain one. */
  envelope: Stationery | null;
  /** Pick an envelope (`null` = plain, on purpose); remembered for this correspondent. */
  chooseEnvelope: (id: string | null) => void;
  /** The envelope follows the paper's book because none was chosen. */
  envelopeMatchesPaper: boolean;
}

export function useStampDesign(opts: { skip?: boolean; relationshipId?: string } = {}): DesignChoice {
  const { data: stamps } = useMyStampsQuery(undefined, { skip: opts.skip });
  const [paperId, setPaperId] = useState<string | null>(() => readPaper(opts.relationshipId));
  useEffect(() => setPaperId(readPaper(opts.relationshipId)), [opts.relationshipId]);
  const paper = useMemo(() => resolvePaper(stamps, paperId), [stamps, paperId]);
  const choosePaper = useCallback(
    (id: string | null) => {
      setPaperId(id);
      writePaper(opts.relationshipId, id);
    },
    [opts.relationshipId],
  );
  const hasPapers = (stamps?.collections ?? []).some((c) => c.papers.length > 0 || c.envelopes.length > 0);
  const [envelopeChoice, setEnvelopeChoice] = useState<string | null>(() => readPair(ENVELOPE_KEY, opts.relationshipId));
  useEffect(() => setEnvelopeChoice(readPair(ENVELOPE_KEY, opts.relationshipId)), [opts.relationshipId]);
  const envelope = useMemo(() => resolveEnvelope(stamps, envelopeChoice, paper), [stamps, envelopeChoice, paper]);
  const chooseEnvelope = useCallback(
    (id: string | null) => {
      const v = id ?? 'plain';
      setEnvelopeChoice(v);
      writePair(ENVELOPE_KEY, opts.relationshipId, v);
    },
    [opts.relationshipId],
  );
  const [chosen, setChosen] = useState<string | null>(readChosen);
  const design = useMemo(() => resolveDesign(stamps, chosen), [stamps, chosen]);
  const chooseDesign = useCallback((id: string | null) => {
    setChosen(id);
    writeChosen(id);
  }, []);
  const hasDesigns = usableCollections(stamps).some((c) => c.designs.length > 0);
  return {
    stamps,
    design,
    chooseDesign,
    hasDesigns,
    paper,
    choosePaper,
    hasPapers,
    envelope,
    chooseEnvelope,
    envelopeMatchesPaper: envelopeChoice == null && envelope != null,
  };
}
